import { useCallback, useState } from "react";

import type { MfaChallengeResponse } from "metabase/api/session";
import { useDispatch, useSelector } from "metabase/redux";
import type { LoginData } from "metabase/redux/auth";
import { login } from "metabase/redux/auth";

import {
  getExternalAuthProviders,
  getHasSessionCookies,
  getIsLdapEnabled,
} from "../../selectors";
import { LoginForm } from "../LoginForm";
import { SmsChallengeForm } from "../SmsChallengeForm";

import { ActionList, ActionListItem } from "./PasswordPanel.styled";

interface PasswordPanelProps {
  redirectUrl?: string;
}

export const PasswordPanel = ({ redirectUrl }: PasswordPanelProps) => {
  const providers = useSelector(getExternalAuthProviders);
  const isLdapEnabled = useSelector(getIsLdapEnabled);
  const hasSessionCookies = useSelector(getHasSessionCookies);
  const dispatch = useDispatch();

  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeResponse | null>(
    null,
  );
  const [remember, setRemember] = useState(false);

  const handleSubmit = useCallback(
    async (data: LoginData) => {
      const { mfaChallenge: challenge } = await dispatch(
        login({ data, redirectUrl }),
      ).unwrap();
      if (challenge) {
        setRemember(Boolean(data.remember));
        setMfaChallenge(challenge);
      }
    },
    [dispatch, redirectUrl],
  );

  if (mfaChallenge) {
    // 99digital: SMS second factor. The EE plugin slot (PLUGIN_MULTI_FACTOR_AUTH) is a no-op
    // placeholder in OSS, and wiring a real component through metabase/plugins risks an import
    // cycle, so render ours directly.
    return (
      <SmsChallengeForm
        challengeToken={mfaChallenge.challenge_token}
        methods={mfaChallenge.methods}
        remember={remember}
        onCancel={() => setMfaChallenge(null)}
      />
    );
  }

  return (
    <div>
      <LoginForm
        isLdapEnabled={isLdapEnabled}
        hasSessionCookies={hasSessionCookies}
        onSubmit={handleSubmit}
      />
      <ActionList>
        {providers.map((provider) => (
          <ActionListItem key={provider.name}>
            <provider.Button redirectUrl={redirectUrl} />
          </ActionListItem>
        ))}
      </ActionList>
    </div>
  );
};
